import React, {
  useState,
  useMemo,
} from 'react';
import { Drawer } from 'antd';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import SageIntacctEmployeeSync from './SageIntacctEmployeeSync';
import SageIntacctProjectSync from './SageIntacctProjectSync';
import SageIntacctContactSync from './SageIntacctContactSync';
import SageIntacctCostcodeSync from './SageIntacctCostcodeSync';
import SageIntacctEquipmentSync from './SageIntacctEquipmentSync';
import SageIntacctMaterialSync from './SageIntacctMaterialSync';

import OnTraccrButton from '../../../common/buttons/OnTraccrButton';
import DrawerFooter from '../../../common/containers/DrawerFooter';
import StepsContainer from '../../../common/containers/StepsContainer';

export default function SageIntacctImportDrawer({
  visible,
  sageIntacctEmployees = [],
  sageIntacctProjects = [],
  sageIntacctCustomers = [],
  sageIntacctVendors = [],
  sageIntacctEquipment = [],
  sageIntacctMaterials = [],
  onClose,
  onSave,
  divisionFilter,
  loading = false,
}) {
  const { t } = useTranslation();

  const [currentStep, setCurrentStep] = useState(0);
  const [importEmployees, setImportEmployees] = useState([]);
  const [importProjects, setImportProjects] = useState([]);
  const [importCustomers, setImportCustomers] = useState([]);
  const [importVendors, setImportVendors] = useState([]);
  const [importCostcodes, setImportCostcodes] = useState([]);
  const [importEquipment, setImportEquipment] = useState([]);
  const [importMaterials, setImportMaterials] = useState([]);

  const steps = useMemo(() => [
    {
      title: 'Employees',
      key: 0,
      content: (
        <SageIntacctEmployeeSync
          sageIntacctEmployees={sageIntacctEmployees}
          onEmployeesChanged={setImportEmployees}
          importEmployees={importEmployees}
          divisionFilter={divisionFilter}
        />
      ),
    },
    {
      title: t('Project', { count: 2 }),
      key: 1,
      content: (
        <SageIntacctProjectSync
          sageIntacctProjects={sageIntacctProjects}
          onProjectsChanged={setImportProjects}
          importProjects={importProjects}
          divisionFilter={divisionFilter}
        />
      ),
    },
    {
      title: 'Cost Codes',
      key: 2,
      content: (
        <SageIntacctCostcodeSync
          onCostcodesChanged={setImportCostcodes}
          importCostcodes={importCostcodes}
          divisionFilter={divisionFilter}
        />
      ),
    },
    {
      title: 'Customers',
      key: 3,
      content: (
        <SageIntacctContactSync
          type="customer"
          sageIntacctContacts={sageIntacctCustomers}
          onContactsChanged={setImportCustomers}
          importContacts={importCustomers}
          divisionFilter={divisionFilter}
        />
      ),
    },
    {
      title: 'Vendors',
      key: 4,
      content: (
        <SageIntacctContactSync
          type="vendor"
          sageIntacctContacts={sageIntacctVendors}
          onContactsChanged={setImportVendors}
          importContacts={importVendors}
          divisionFilter={divisionFilter}
        />
      ),
    },
    {
      title: 'Equipment',
      key: 5,
      content: (
        <SageIntacctEquipmentSync
          sageIntacctEquipment={sageIntacctEquipment}
          onEquipmentChanged={setImportEquipment}
          importEquipment={importEquipment}
          divisionFilter={divisionFilter}
        />
      ),
    },
    {
      title: 'Materials',
      key: 6,
      content: (
        <SageIntacctMaterialSync
          sageIntacctMaterials={sageIntacctMaterials}
          onMaterialsChanged={setImportMaterials}
          importMaterials={importMaterials}
          divisionFilter={divisionFilter}
        />
      ),
    },
  ], [
    sageIntacctEmployees,
    sageIntacctProjects,
    sageIntacctCustomers,
    sageIntacctVendors,
    sageIntacctEquipment,
    sageIntacctMaterials,
    importEmployees,
    importProjects,
    importCostcodes,
    importCustomers,
    importVendors,
    importEquipment,
    importMaterials,
    divisionFilter,
  ]);

  return (
    <Drawer
      title="Import from Sage Intacct"
      visible={visible}
      width="90%"
      onClose={onClose}
    >
      <div style={{ height: 'calc(100% - 55px)', width: '100%', overflowY: 'scroll' }}>
        <StepsContainer
          currentStep={currentStep}
          steps={steps}
        />
      </div>
      <DrawerFooter>
        {currentStep > 0 && (
          <OnTraccrButton
            type="back"
            title="Back"
            onClick={() => setCurrentStep(currentStep - 1)}
            style={{ marginRight: 8 }}
          />
        )}
        <OnTraccrButton
          title={currentStep === steps.length - 1 ? 'Sync' : 'Next'}
          loading={loading && currentStep === steps.length - 1}
          disabled={loading}
          onClick={() => {
            if (currentStep === steps.length - 1) {
              return onSave({
                importUsers: importEmployees,
                importProjects,
                importCustomers,
                importVendors,
                importCostcodes,
                importEquipment,
                importMaterials,
              });
            }
            setCurrentStep(currentStep + 1);
          }}
        />
      </DrawerFooter>
    </Drawer>
  );
}

/* eslint-disable react/forbid-prop-types */
SageIntacctImportDrawer.propTypes = {
  visible: PropTypes.bool.isRequired,
  sageIntacctEmployees: PropTypes.array,
  sageIntacctProjects: PropTypes.array,
  sageIntacctCustomers: PropTypes.array,
  sageIntacctVendors: PropTypes.array,
  sageIntacctEquipment: PropTypes.array,
  sageIntacctMaterials: PropTypes.array,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  divisionFilter: PropTypes.instanceOf(Set),
  loading: PropTypes.bool,
};

SageIntacctImportDrawer.defaultProps = {
  sageIntacctEmployees: [],
  sageIntacctProjects: [],
  sageIntacctCustomers: [],
  sageIntacctVendors: [],
  sageIntacctEquipment: [],
  sageIntacctMaterials: [],
  divisionFilter: null,
  loading: false,
};
